import { ICreateFamilyRelation, IUser, IUserRelation, IUserUpdate } from 'client/model/User';
import { IPersonExistsResponse, ICreateFamilyMemberRequest, IFamilyMemberRequest, IApiResponse } from 'client/model/FamilyMember';
import axiosInstance from '../config/axiosInstance';
import { IResponse } from 'client/model/Response';
import { IUserService, PlanningMode } from 'client/model/service';
import { API_ENDPOINTS } from '../../shared/apiEndpoints';

const USERS_ROOT = '/users';

function isRecord(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === 'object' && !Array.isArray(v);
}

function isEmptyMetadataValue(v: unknown): boolean {
  if (v == null) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.length === 0;
  return false;
}

/** Ne remplace pas une clé déjà renseignée par une valeur vide (ex. commentaire effacé dans un 2e bloc). */
function mergeMetadataChunk(
  base: Record<string, unknown>,
  addition: Record<string, unknown>
): Record<string, unknown> {
  const out = { ...base };
  for (const [k, v] of Object.entries(addition)) {
    if (!isEmptyMetadataValue(v)) {
      out[k] = v;
    } else if (!(k in out)) {
      out[k] = v;
    }
  }
  return out;
}

/** Fusionne metadata / user_metadata présents à la racine de la réponse API dans l’objet utilisateur. */
function mergeUserMetadataFromEnvelope(user: IUser, envelope: Record<string, unknown>): IUser {
  let merged: Record<string, unknown> = {};
  const push = (chunk: unknown) => {
    if (!isRecord(chunk)) return;
    merged = mergeMetadataChunk(merged, chunk);
  };
  push(user.metadata);
  push(envelope.metadata);
  push(envelope.user_metadata);
  const data = envelope.data;
  if (isRecord(data)) {
    push(data.metadata);
    push(data.user_metadata);
  }
  if (Object.keys(merged).length === 0) return user;
  return { ...user, metadata: merged };
}

/** Réponse enrichie GET /users/{id}/family-members : `data` = membres (UserResource + metadata), `meta.user` = utilisateur courant. */
function parseFamilyMembersResponse(payload: unknown): { members: IUser[]; viewer: IUser | null } {
  if (!isRecord(payload)) {
    return { members: [], viewer: null };
  }
  const raw = payload.data;
  const list = Array.isArray(raw) ? raw : [];
  const members = list.map((row) => {
    if (!isRecord(row)) {
      return row as unknown as IUser;
    }
    const user = row as unknown as IUser;
    return mergeUserMetadataFromEnvelope(user, row);
  });
  let viewer: IUser | null = null;
  const meta = payload.meta;
  if (isRecord(meta) && meta.user != null && isRecord(meta.user) && 'id' in meta.user) {
    const u = meta.user as unknown as IUser;
    viewer = mergeUserMetadataFromEnvelope(u, meta.user as Record<string, unknown>);
  }
  return { members, viewer };
}

const userService = {
  /**
   * Récupère les détails d'un utilisateur par ID.
   * @param userId - Identifiant de l'utilisateur
   */
  getById: async (userId: number) => {
    try {
      const response = await axiosInstance.get<IUser | { data: IUser }>(
        `${USERS_ROOT}/${userId}/details`
      );
      const body = response.data as unknown;
      const b = body as Record<string, unknown> | null;
      const nested = b?.data;
      let user: IUser;
      if (
        nested != null &&
        typeof nested === 'object' &&
        !Array.isArray(nested) &&
        'id' in nested
      ) {
        user = nested as IUser;
      } else {
        user = body as IUser;
      }
      if (b) {
        user = mergeUserMetadataFromEnvelope(user, b);
      }
      return user;
    } catch (error: any) {
      throw new Error(`Erreur: ${error.message || 'Unknown error'}`);
    }
  },

  /**
   * Récupère les relations familiales d'un utilisateur.
   * @param userId - Identifiant de l'utilisateur
   */
  getFamily: async (userId: number) => {
    try {
      const response = await axiosInstance.get(`${USERS_ROOT}/${userId}/family-members`);
      const { members } = parseFamilyMembersResponse(response.data);
      return members;
    } catch (error) {
      console.error('Erreur lors de la récupération des relations familiales :', error);
      throw error;
    }
  },

  /**
   * Récupère les relations disponibles pour un utilisateur.
   * @param userId - ID de l'utilisateur.
   * @returns Une liste des relations disponibles.
   */
  getAvailableRelations: async (userId: number) => {
    try {
      const response = await axiosInstance.get<IResponse<IUser>>(`/users/${userId}/available-relations`);
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch available relations:', error);
      throw error;
    }
  },

  /**
   * Supprime une relation utilisateur par ID.
   * @param relationId - Identifiant de la relation à supprimer
   */
  deleteUserRelation: async (relationId: number) => {
    try {
      const response = await axiosInstance.delete(`${USERS_ROOT}/relations/${relationId}`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la suppression de la relation avec ID ${relationId}:`, error);
      throw error;
    }
  },

  createFamilyRelations: async (userId: number, relations: ICreateFamilyRelation[]) => {
    try {
      const response = await axiosInstance.post<IResponse<IUserRelation>>(`/users/user-relations`, {
        user_id: userId,
        relations
      });
      return response.data;
    } catch (error) {
      console.error('Failed to create family relations:', error);
      throw error; // Propager l'erreur pour être gérée par l'appelant
    }
  },

  updateUser: async (userId: number, updateData: Partial<IUserUpdate>) => {
    try {
      const response = await axiosInstance.put(`${USERS_ROOT}/${userId}`, updateData);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la modification de l'utilisateur avec ID ${userId}:`, error);
      throw error;
    }
  },

  uploadAvatar: async (userId: number, avatar: Blob) => {
    const formData = new FormData();
    formData.append('avatar', avatar);

    try {
      const response = await axiosInstance.post<Partial<IUser>>(`${USERS_ROOT}/${userId}/avatar`, formData);
      return response.data;
    } catch (error) {
      console.error('Erreur réseau :', error);
      throw error;
    }
  },
  getUserServicePosition: async (userId: number) => {
    try {
      const response = await axiosInstance.get<IResponse<IUserService>>(`${USERS_ROOT}/${userId}/service-positions`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création du service :', error);
      throw error;
    }
  },

  /**
   * MOM-407 — Met à jour le paramétrage global de planification d'un poste
   * (« Autant que nécessaire » ou « Fois par mois » + nombre max par mois).
   * @param pivotId - Identifiant du pivot service_position__user
   */
  updateServicePositionPlanning: async (
    pivotId: number,
    payload: { planning_mode: PlanningMode; max_times_per_month: number | null }
  ) => {
    const response = await axiosInstance.put(`${USERS_ROOT}/service-position-pivots/${pivotId}`, payload);
    return response.data;
  },

  /**
   * Récupère les relations familiales d'un utilisateur.
   * @param userId - Identifiant de l'utilisateur
   */
  getMyFamily: async (userId: number) => {
    try {
      const response = await axiosInstance.get(`${USERS_ROOT}/${userId}/family-members`);
      const { members } = parseFamilyMembersResponse(response.data);
      return members;
    } catch (error) {
      console.error('Erreur lors de la récupération des relations familiales :', error);
      throw error;
    }
  },

  /**
   * Récupère d'un utilisateur connecter
   */
  getCurrentUser: async () => {
    const storedUser = localStorage.getItem("user");
    // 2. Vérifier si elle existe
    if (storedUser) {
      // 3. Convertir la chaîne JSON en objet JS
      const user = JSON.parse(storedUser);
      return user;
    }

    return null;
  },

  /**
   * Récupère notifications d'un utilisateur connecter
   */
  getNotifications: async () => {
    // Mode invite : les notifications sont par nature liees a un compte. Sans
    // jeton, l'appel partait quand meme et repondait 401 sur chaque ecran
    // (constate le 07/10 dans le journal reseau, en conditions d'invite).
    if (!localStorage.getItem('api_token')) {
      return [];
    }

    try {
      const response = await axiosInstance.get<IResponse<IUserRelation>>(`${USERS_ROOT}/notifications?status=unread`);
      return response.data.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des relations familiales :', error);
      throw error;
    }
  },

  /**
   * Nombre de notifications non montrées de l'utilisateur (Notification::getNotShownCountByUser
   * côté back — recalculé indépendamment du filtre `status`), pour synchroniser le badge de
   * l'icône de l'app (Push iOS #MOM-465). `per_page=1` : seul `meta.not_shown_count` nous
   * intéresse, pas la liste.
   */
  /**
   * Marque toutes les notifications de l'utilisateur comme vues.
   *
   * Appelé à l'ouverture de l'écran Notifications : sans cela, `shown_at`
   * n'était renseigné que par les actions accepter/refuser, et la pastille de
   * l'icône ne pouvait que croître.
   *
   * Best-effort : un échec ne doit jamais empêcher l'affichage des
   * notifications.
   */
  markAllNotificationsShown: async (): Promise<void> => {
    try {
      await axiosInstance.put(`${USERS_ROOT}/notifications/mark-all-shown`);
    } catch (error) {
      console.error('Erreur lors du marquage des notifications :', error);
    }
  },

  getNotShownNotificationCount: async (): Promise<number> => {
    try {
      const response = await axiosInstance.get<IResponse<unknown> & { meta?: { not_shown_count?: number } }>(
        `${USERS_ROOT}/notifications?per_page=1`
      );
      return response.data.meta?.not_shown_count ?? 0;
    } catch (error) {
      console.error('Erreur lors de la récupération du compteur de notifications :', error);
      return 0;
    }
  },

  /**
   * Récupère notifications d'un utilisateur connecter
   */
  getNotificationsReady: async () => {
    try {
      const response = await axiosInstance.get<IResponse<IUserRelation>>(`${USERS_ROOT}/notifications?status=read`);
      return response.data.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des relations familiales :', error);
      throw error;
    }
  },

  // NOUVELLES MÉTHODES POUR LA GESTION DES MEMBRES DE FAMILLE
  /**
   * Vérifie si une personne existe dans la base de données
   * @param firstname - Prénom de la personne
   * @param lastname - Nom de la personne
   */
  checkPersonExists: async (firstname: string, lastname: string) => {
    try {
      const response = await axiosInstance.post<IApiResponse<IPersonExistsResponse>>(API_ENDPOINTS.USERS.checkPersonExists, {
        firstname,
        lastname
      });
      // Retourner directement les données au lieu de response.data
      return response.data.data;
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'existence de la personne :', error);
      throw error;
    }
  },

  /**
   * Ajoute directement une personne existante à la famille
   * @param userId - ID de l'utilisateur principal
   * @param relatedUserId - ID de la personne à ajouter
   * @param relationshipType - Type de relation
   */
  addExistingPersonToFamily: async (userId: number, relatedUserId: number, relationshipType: string) => {
    try {
      const credentials = {
        members: [{
          id: relatedUserId,
          status: relationshipType
        }]
      };
      
      const response = await axiosInstance.post<IResponse<IUserRelation>>(API_ENDPOINTS.USERS.addFamilyMembers(userId), credentials);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la personne existante à la famille :', error);
      throw error;
    }
  },

  /**
   * Crée une demande d'ajout de membre de famille
   * @param requestData - Données de la demande
   */
  createFamilyMemberRequest: async (requestData: ICreateFamilyMemberRequest) => {
    try {
      const formData = new FormData();
      formData.append('user_id', requestData.user_id.toString());
      formData.append('firstname', requestData.firstname);
      formData.append('lastname', requestData.lastname);
      // Mapper les types de relation selon les spécifications
      const mappedRelationshipType: string = requestData.relationship_type;
     
      
      formData.append('relationship_type', mappedRelationshipType);
      
      if (requestData.date_of_birth) {
        formData.append('date_of_birth', requestData.date_of_birth);
      }
      
      if (requestData.avatar) {
        formData.append('avatar', requestData.avatar);
      }

      if (requestData.user_ids && requestData.user_ids.length > 0) {
        // Envoyer chaque ID individuellement pour que le backend reçoive un tableau
        requestData.user_ids.forEach((userId, index) => {
          formData.append(`user_ids[${index}]`, userId.toString());
        });
      }

      const response = await axiosInstance.post<IResponse<IFamilyMemberRequest>>(
        API_ENDPOINTS.USERS.createFamilyMemberRequest,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de la demande d\'ajout :', error);
      throw error;
    }
  },

  /**
   * Récupère les demandes d'ajout en attente pour un utilisateur
   * @param userId - ID de l'utilisateur
   */
  getPendingFamilyRequests: async (userId: number) => {
    try {
      const response = await axiosInstance.get<IResponse<IFamilyMemberRequest[]>>(
        API_ENDPOINTS.USERS.getPendingFamilyRequests(userId)
      );
      return response.data.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des demandes en attente :', error);
      throw error;
    }
  },

  /**
   * Approuve une demande d'ajout de membre de famille
   * @param requestId - ID de la demande
   */
  approveFamilyRequest: async (requestId: number) => {
    try {
      const response = await axiosInstance.post<IResponse<IFamilyMemberRequest>>(
        API_ENDPOINTS.USERS.approveFamilyRequest(requestId)
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'approbation de la demande :', error);
      throw error;
    }
  },

  /**
   * Refuse une demande d'ajout de membre de famille
   * @param requestId - ID de la demande
   */
  rejectFamilyRequest: async (requestId: number) => {
    try {
      const response = await axiosInstance.post<IResponse<IFamilyMemberRequest>>(
        API_ENDPOINTS.USERS.rejectFamilyRequest(requestId)
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors du refus de la demande :', error);
      throw error;
    }
  },

  /**
   * Supprime une notification
   * @param notificationId - ID de la notification à supprimer
   */
  deleteNotification: async (notificationId: string) => {
    try {
      const response = await axiosInstance.delete<IResponse<any>>(
        `${USERS_ROOT}/notifications/${notificationId}`
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la suppression de la notification :', error);
      throw error;
    }
  },

  /**
   * Supprime le compte d'un utilisateur
   * @param userId - ID de l'utilisateur à supprimer
   */
  deleteAccount: async (userId: number) => {
    try {
      const response = await axiosInstance.delete<IResponse<any>>(
        `${USERS_ROOT}/${userId}`
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la suppression du compte :', error);
      throw error;
    }
  }
};

export default userService;
